import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Clock, CheckCircle2, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const SetupAutonomousCron = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [cronStatus, setCronStatus] = useState<'unknown' | 'active' | 'inactive'>('unknown');

  const checkCronStatus = async () => {
    try {
      // Check if cron job exists by querying the operations table instead
      const { data, error } = await supabase.functions.invoke('autonomous-audio-generator', {
        body: { action: 'check-status' }
      });

      if (error) throw error;

      // If we get a response, the system is set up
      const hasActiveCron = data?.systemReady || false;
      setCronStatus(hasActiveCron ? 'active' : 'inactive');
      return hasActiveCron;
    } catch (error) {
      console.error('Error checking cron status:', error);
      setCronStatus('inactive');
      return false;
    }
  };

  const setupCronJobs = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('setup-auto-resume-cron', {
        body: {}
      });

      if (error) throw error;

      if (!data.success) {
        throw new Error(data.error || 'Failed to setup cron jobs');
      }

      toast.success('Autonomous System Activated! 🚀', {
        description: `${data.totalCrons} cron jobs created. Audio will auto-generate every 3 minutes.`,
        duration: 5000
      });

      await checkCronStatus();
    } catch (error: any) {
      console.error('Error setting up cron jobs:', error);
      toast.error('Failed to setup cron jobs', {
        description: error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Autonomous Cron Job System
        </CardTitle>
        <CardDescription>
          Set up automated background workers that run every 3 minutes. This enables browser-independent audio generation.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-4">
          <Button
            onClick={setupCronJobs}
            disabled={isLoading}
            className="gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Setting up cron jobs...
              </>
            ) : (
              <>
                <Clock className="h-4 w-4" />
                Activate Autonomous System
              </>
            )}
          </Button>

          <Button
            onClick={checkCronStatus}
            variant="outline"
            disabled={isLoading}
            className="gap-2"
          >
            Check Status
          </Button>
        </div>

        {cronStatus !== 'unknown' && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-muted">
            {cronStatus === 'active' ? (
              <>
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="text-sm">Cron job is active - system is autonomous</span>
              </>
            ) : (
              <>
                <AlertCircle className="h-4 w-4 text-yellow-600" />
                <span className="text-sm">Cron job not found - click "Activate" to enable</span>
              </>
            )}
          </div>
        )}

        <div className="text-sm text-muted-foreground space-y-1">
          <p className="font-medium">What this does:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Creates <code className="text-xs bg-muted px-1 rounded">auto-audio-generation</code> cron job (runs every 3 min)</li>
            <li>Processes ~10 audio modules per run (~200/hour)</li>
            <li>Auto-resumes after failures or browser close</li>
            <li>No need to keep browser open!</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
